import { ApolloLink } from "@apollo/client";
import { Observable } from "zen-observable-ts";

interface RouteChangeAbortError extends Error {
  name: "RouteChangeAbortError";
  operationName?: string;
  requestDuration: number;
}

class RouteChangeManager {
  private currentRoute: string;
  private callbacks: Set<() => void>;

  constructor() {
    this.currentRoute = window.location.pathname;
    this.callbacks = new Set();
  }

  notifyRouteChange(newRoute: string): void {
    if (this.currentRoute !== newRoute) {
      console.log(`Route changed from ${this.currentRoute} to ${newRoute}`);
      this.currentRoute = newRoute;
      this.callbacks.forEach((callback) => {
        try {
          callback();
        } catch (error) {
          console.warn("Route change callback error:", error);
        }
      });
    }
  }

  addCallback(callback: () => void): void {
    this.callbacks.add(callback);
  }

  removeCallback(callback: () => void): void {
    this.callbacks.delete(callback);
  }

  getActiveCallbacksCount(): number {
    return this.callbacks.size;
  }

  // Check if route has changed
  hasRouteChanged(): boolean {
    return window.location.pathname !== this.currentRoute;
  }
}

export const routeManager = new RouteChangeManager();
export const notifyRouteChange = (route: string) =>
  routeManager.notifyRouteChange(route);

export const routeAbortLink = new ApolloLink((operation, forward) => {
  return new Observable((observer) => {
    let aborted = false;
    const requestStartTime = Date.now();

    // If route changed before the request began -- delete it
    if (routeManager.hasRouteChanged()) {
      aborted = true;
      const requestDuration = Date.now() - requestStartTime;
      const operationName = operation.operationName || "Unknown Operation";

      console.log(
        `Aborting GraphQL operation "${operationName}" due to route change after ${requestDuration}ms`,
      );
      observer.error(new Error("Request aborted due to route change"));
      return;
    }

    const subscription = forward(operation).subscribe({
      next: (result) => {
        if (!aborted) {
          observer.next(result);
        }
      },
      error: (error) => {
        if (!aborted) {
          observer.error(error);
        }
      },
      complete: () => {
        if (!aborted) {
          observer.complete();
        }
      },
    });

    // create a handler that unsubs if the route changes
    const onRouteChange = () => {
      if (!aborted) {
        aborted = true;
        const requestDuration = Date.now() - requestStartTime;
        const operationName = operation.operationName || "Unknown Operation";

        console.log(
          `Aborting GraphQL operation "${operationName}" after ${requestDuration}ms due to route change`,
        );

        subscription.unsubscribe();

        // Create a properly typed error for route changes
        const abortError: RouteChangeAbortError = new Error(
          "Request aborted due to route change",
        ) as RouteChangeAbortError;
        abortError.name = "RouteChangeAbortError";
        abortError.operationName = operationName;
        abortError.requestDuration = requestDuration;

        observer.error(abortError);
      }
    };

    routeManager.addCallback(onRouteChange);

    // Cleanup function
    return () => {
      routeManager.removeCallback(onRouteChange);
      if (!aborted) {
        subscription.unsubscribe();
      }
    };
  });
});
